// The content pack (E01-S3-T5; contracts 1.0 section 6): the signed manifest, the dictionary and
// screen descriptions, note templates, personas, phrases, the firm overlay and retrieval chunks.
import { z } from "zod";
import { Citation, AnyCitation } from "./citation.js";
import { ExprField } from "./expr/schema.js";
import { checkTemplateRefs } from "./expr/template.js";
import { Count, Id, IsoUtc, NonEmpty, strictObject, version } from "./primitives.js";
import { RefusalReason } from "./refusal.js";
import { Audience } from "./sop.js";
import { Sha256 } from "./training.js";
import { SopStepRef } from "./tripwire.js";
/** `2023.01+7`: the guide's edition, then the build number. */
export const PACK_VERSION_RE = /^\d{4}\.\d{2}\+\d+$/;
export const PackVersion = z
    .string()
    .regex(PACK_VERSION_RE, "expected a pack version such as 2023.01+7");
export const PackManifest = strictObject({
    v: version(1),
    id: Id,
    version: PackVersion,
    source: strictObject({ doc: NonEmpty, docVersion: NonEmpty, pages: z.number().int().positive() }),
    builtAt: IsoUtc,
    builtBy: NonEmpty,
    review: z.array(strictObject({ reviewer: NonEmpty, approvedAt: IsoUtc, scope: NonEmpty })),
    contents: strictObject({
        sops: Count,
        steps: Count,
        screens: Count,
        fields: Count,
        tripwires: Count,
        titles: Count,
        beats: Count,
        questions: Count.optional(), // ADR-023: the questions across the pack's knowledge checks
    }),
    variants: z.array(Id).min(1),
    media: z.array(strictObject({ key: NonEmpty, sha256: Sha256, bytes: Count })),
    library: NonEmpty,
    // over sha256(canonical manifest without signature ‖ content) (D-77)
    signature: strictObject({ alg: z.literal("ed25519"), keyId: NonEmpty, value: NonEmpty }),
}).superRefine((m, ctx) => {
    const seen = new Set();
    m.media.forEach((x, i) => {
        if (seen.has(x.key))
            ctx.addIssue({
                code: "custom",
                message: `duplicate media key ${x.key}`,
                path: ["media", i, "key"],
            });
        seen.add(x.key);
    });
});
export const FieldEntry = strictObject({
    id: Id,
    label: NonEmpty,
    guide: NonEmpty,
    plain: NonEmpty.optional(),
    citation: Citation,
    screens: z.array(Id),
});
export const ScreenEntry = strictObject({
    id: Id,
    title: NonEmpty,
    blurb: NonEmpty,
    citation: Citation.optional(),
});
/** Placeholders a note template may use beyond the allowed frame paths (D-63, D-80). */
export const NOTE_TEMPLATE_EXTRAS = ["user.id", "walkthrough.ref"];
/** Ascendis caps a note at 255 characters. */
export const NOTE_MAX_LENGTH = 255;
export const NoteTemplate = strictObject({
    id: Id,
    when: ExprField,
    text: NonEmpty,
    maxLength: z.number().int().positive().max(NOTE_MAX_LENGTH),
}).superRefine((n, ctx) => {
    for (const e of checkTemplateRefs(n.text, NOTE_TEMPLATE_EXTRAS))
        ctx.addIssue({
            code: "custom",
            message: `note ${n.id}: ${e.code}: ${e.message}`,
            path: ["text"],
        });
    if (!n.text.endsWith("{walkthrough.ref}"))
        ctx.addIssue({
            code: "custom",
            message: `note ${n.id}: a note ends with {walkthrough.ref} (D-80)`,
            path: ["text"],
        });
});
export const PersonaId = z.enum(["ops", "desk", "warden", "architect", "pilot"]);
/** Things the ORCHESTRATOR may do for a persona; never model tool calls. */
export const Capability = z.enum([
    "sop.walk",
    "sop.lookup",
    "field.explain",
    "beat.play",
    "title.offer",
    "note.draft",
    "navigate",
    "escalate.name",
    "answer.compose",
]);
export const WorkingMode = z.enum([
    "work-with-me",
    "guided-walkthrough",
    "explain",
    "onboard",
    "ask-anything",
]);
const unique = (item, what) => z.array(item).superRefine((xs, ctx) => {
    if (new Set(xs).size !== xs.length)
        ctx.addIssue({ code: "custom", message: `each ${what} appears once` });
});
export const Persona = strictObject({
    id: PersonaId,
    displayName: NonEmpty,
    roleLine: NonEmpty,
    disclosure: NonEmpty,
    lines: strictObject({
        greeting: NonEmpty,
        paused: NonEmpty,
        unknownScreen: NonEmpty,
        modelUnavailable: NonEmpty,
        refusals: z.record(RefusalReason, NonEmpty), // exhaustive: every reason has a line
    }),
    voiceRef: NonEmpty.optional(),
    capabilities: unique(Capability, "capability"),
    modes: unique(WorkingMode, "mode").min(1),
});
export const Phrases = strictObject({
    actRequests: z.array(NonEmpty),
    connectives: z.record(Id, NonEmpty),
});
/**
 * A label a firm overlay may set: one line, short. Terminology changes labels — field names, enum
 * values — never the text of an instruction (D-78).
 */
export const OVERLAY_LABEL_MAX = 80;
export const OverlayLabel = NonEmpty.max(OVERLAY_LABEL_MAX).regex(/^[^\r\n]*$/, "a label is one line");
/**
 * What a firm may change. Deliberately has no field that could carry a citation or an
 * instruction edit; pack.test.ts pins the key list so adding one is a visible contract change.
 */
export const FirmOverlay = strictObject({
    v: version(1),
    packId: Id,
    packVersion: PackVersion,
    orgId: NonEmpty,
    meta: strictObject({ author: NonEmpty, approvedBy: NonEmpty, at: IsoUtc }),
    terminology: z.record(OverlayLabel, OverlayLabel).optional(), // label → label; a key cannot be a sentence (D-78)
    firmRules: z
        .array(strictObject({ sopStep: SopStepRef, label: NonEmpty, value: NonEmpty, source: NonEmpty }))
        .optional(),
    enumLabels: z.record(Id, z.array(OverlayLabel).min(1)).optional(),
    suppress: strictObject({
        sopIds: z.array(Id).optional(),
        titleIds: z.array(Id).optional(),
    }).optional(),
});
export const ContentChunkKind = z.enum([
    "sop-step",
    "field",
    "guide-text",
    "tripwire",
    "title",
    "beat",
]);
export const ContentChunk = strictObject({
    id: NonEmpty,
    packId: Id,
    packVersion: PackVersion,
    kind: ContentChunkKind,
    ref: NonEmpty,
    text: NonEmpty,
    citation: AnyCitation, // a page of the guide, or a firm rule's document (ADR-027)
    library: NonEmpty,
    audience: z.array(Audience).min(1),
});
//# sourceMappingURL=pack.js.map